import { Injectable } from '@angular/core';
import { Timezone } from '@sc/types';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';
import { environment } from '../../../environments/environment';

dayjs.extend(utc);
dayjs.extend(timezone);

@Injectable({
  providedIn: 'root',
})
export class CalendarLinksService {
  getLink(
    platform: 'google' | 'apple' | 'outlook',
    config: {
      title: string;
      description: string;
      email: string;
      start: string;
      end: string;
      timezone: Timezone;
      link: string;
    }
  ) {
    // Handle missing timezone gracefully with fallback to Eastern Time
    const timezone = config.timezone || {
      name: 'Eastern Time',
      tzid: 'America/New_York',
    };

    // Parse dates and convert to UTC for calendar links
    const startDate = dayjs.tz(config.start, 'YYYY-MM-DD h:mm A', timezone.tzid);
    const endDate = dayjs.tz(config.end, 'YYYY-MM-DD h:mm A', timezone.tzid);

    // Validate parsed dates
    if (!startDate.isValid() || !endDate.isValid()) {
      throw new Error(`Invalid date format. Start: ${config.start}, End: ${config.end}`);
    }

    // Format dates for different calendar systems
    const googleDateFormat = 'YYYYMMDDTHHmmss';
    const outlookDateFormat = 'YYYY-MM-DDTHH:mm:ss';

    // Prepare the description with the join link
    const fullDescription = `👇👇👇 Click this link to Join\n${config.link}\n\n${config.description}`;

    switch (platform) {
      case 'google': {
        // Google Calendar web link format
        const googleParams = new URLSearchParams({
          action: 'TEMPLATE',
          text: config.title,
          dates: `${startDate.utc().format(googleDateFormat) + 'Z'}/${endDate.utc().format(googleDateFormat) + 'Z'}`,
          details: fullDescription,
          location: config.link,
          ctz: timezone.tzid,
        });
        return `https://calendar.google.com/calendar/render?${googleParams.toString()}`;
      }

      case 'apple': {
        // Generate server endpoint URL for .ics file download
        const params = {
          title: config.title,
          description: config.description,
          email: config.email,
          start: config.start,
          end: config.end,
          timezone: timezone.tzid,
          link: config.link,
        };

        const ts = Math.floor(Date.now() / 1000);
        const calendarParams = new URLSearchParams({ ...params, ts: ts.toString() });

        // Use the appropriate Firebase function endpoint
        const baseUrl =
          environment.production || environment.beta || environment.staging
            ? `${location.origin}/functions/calendar`
            : 'http://localhost:8202/squadcast-dev/us-central1/calendar';

        return `${baseUrl}?${calendarParams.toString()}`;
      }

      case 'outlook': {
        // Outlook.com web link format
        // Note: Outlook doesn't make URLs in the body clickable, but the location field is clickable
        const outlookDescription = config.description || '🔴 Recorded on SquadCast';
        const outlookParams = new URLSearchParams({
          path: '/calendar/action/compose',
          rru: 'addevent',
          subject: config.title,
          body: outlookDescription,
          location: config.link,
          startdt: startDate.utc().format(outlookDateFormat) + 'Z',
          enddt: endDate.utc().format(outlookDateFormat) + 'Z',
        });
        return `https://outlook.office.com/calendar/0/deeplink/compose?${outlookParams.toString()}`;
      }

      default:
        throw new Error(`Unsupported calendar platform: ${platform}`);
    }
  }
}
